import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from './supabaseClient';

function AdminCreateAccount() {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setResult(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setResult({ type: 'error', message: 'Not authenticated. Please log in again.' });
        setLoading(false);
        return;
      }

      const response = await fetch('/.netlify/functions/create-free-account', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setResult({
          type: 'success',
          message: `Account created for ${data.email}.`,
          userId: data.userId,
        });
        setFormData({ fullName: '', email: '', password: '' });
      } else {
        setResult({ type: 'error', message: data.error || 'Failed to create account' });
      }
    } catch (error) {
      setResult({ type: 'error', message: 'Network error: ' + error.message });
    }

    setLoading(false);
  };

  return (
    <div>
      <h1 style={styles.title}>Create Free Account</h1>
      <p style={styles.subtitle}>Create a PASS Profile account that bypasses Stripe payment</p>

      <div style={styles.formCard}>
        <form onSubmit={handleSubmit}>
          <div style={styles.formGroup}>
            <label style={styles.label} htmlFor="fullName">Full Name</label>
            <input
              style={styles.input}
              type="text"
              id="fullName"
              name="fullName"
              value={formData.fullName}
              onChange={handleChange}
              placeholder="John Smith"
              required
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label} htmlFor="email">Email Address</label>
            <input
              style={styles.input}
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="john@example.com"
              required
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label} htmlFor="password">Password</label>
            <input
              style={styles.input}
              type="text"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Create a password for the user"
              required
              minLength="6"
            />
            <span style={styles.hint}>Minimum 6 characters. You'll share this with the user.</span>
          </div>

          <button
            type="submit"
            disabled={loading}
            style={{
              ...styles.submitBtn,
              ...(loading ? styles.submitBtnDisabled : {}),
            }}
          >
            {loading ? 'Creating...' : 'Create Free Account'}
          </button>
        </form>

        {result && (
          <div style={{
            ...styles.resultBox,
            ...(result.type === 'success' ? styles.resultSuccess : styles.resultError),
          }}>
            <p style={styles.resultText}>{result.message}</p>
            {result.type === 'success' && result.userId && (
              <div style={styles.resultActions}>
                <Link to={`/admin/users/${result.userId}`} style={styles.resultLink}>
                  View User Details
                </Link>
                <Link to="/admin/users" style={styles.resultLinkSecondary}>
                  View All Users
                </Link>
              </div>
            )}
          </div>
        )}
      </div>

      <div style={styles.instructionsCard}>
        <h3 style={styles.instructionsTitle}>After creating the account:</h3>
        <ol style={styles.instructionsList}>
          <li>Share login credentials with the user</li>
          <li>User logs in at: <strong style={{ color: '#2A9D8F' }}>pass.tedsvoiceacademy.com/login</strong></li>
          <li>User completes the 60-question assessment</li>
          <li>You can trigger their report from the user detail page</li>
        </ol>
      </div>
    </div>
  );
}

const styles = {
  title: {
    fontSize: 24,
    fontWeight: 700,
    color: '#0B3D2E',
    margin: '0 0 4px 0',
  },
  subtitle: {
    fontSize: 14,
    color: '#6B7A8F',
    margin: '0 0 24px 0',
  },
  formCard: {
    background: 'white',
    borderRadius: 10,
    padding: 24,
    border: '1px solid #E8ECF0',
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
    marginBottom: 24,
    maxWidth: 500,
  },
  formGroup: {
    marginBottom: 20,
  },
  label: {
    display: 'block',
    fontSize: 13,
    fontWeight: 600,
    color: '#0B3D2E',
    marginBottom: 6,
  },
  input: {
    width: '100%',
    padding: '10px 14px',
    border: '1px solid #D0D5DD',
    borderRadius: 6,
    fontSize: 14,
    fontFamily: 'inherit',
    outline: 'none',
    boxSizing: 'border-box',
  },
  hint: {
    display: 'block',
    fontSize: 12,
    color: '#6B7A8F',
    marginTop: 4,
  },
  submitBtn: {
    background: '#2A9D8F',
    color: 'white',
    border: 'none',
    padding: '12px 24px',
    borderRadius: 6,
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    width: '100%',
    marginTop: 4,
  },
  submitBtnDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
  resultBox: {
    marginTop: 20,
    padding: 16,
    borderRadius: 8,
  },
  resultSuccess: {
    background: '#E8F5E9',
    border: '1px solid #A5D6A7',
  },
  resultError: {
    background: '#FFF3F3',
    border: '1px solid #FFCDD2',
  },
  resultText: {
    margin: '0 0 8px 0',
    fontSize: 14,
    fontWeight: 600,
    color: '#0B3D2E',
  },
  resultActions: {
    display: 'flex',
    gap: 12,
    marginTop: 12,
  },
  resultLink: {
    display: 'inline-block',
    background: '#0B3D2E',
    color: 'white',
    padding: '8px 16px',
    borderRadius: 6,
    fontSize: 13,
    fontWeight: 600,
    textDecoration: 'none',
  },
  resultLinkSecondary: {
    display: 'inline-block',
    background: 'transparent',
    color: '#0B3D2E',
    padding: '8px 16px',
    borderRadius: 6,
    fontSize: 13,
    fontWeight: 600,
    textDecoration: 'none',
    border: '1px solid #0B3D2E',
  },
  instructionsCard: {
    background: 'white',
    borderRadius: 10,
    padding: 24,
    border: '1px solid #E8ECF0',
    maxWidth: 500,
  },
  instructionsTitle: {
    fontSize: 14,
    fontWeight: 700,
    color: '#0B3D2E',
    margin: '0 0 12px 0',
  },
  instructionsList: {
    margin: 0,
    paddingLeft: 20,
    color: '#333',
    lineHeight: 2,
    fontSize: 14,
  },
};

export default AdminCreateAccount;
